import { Injectable } from '@angular/core';
import { ApiService } from '@core/http';
import { Observable } from 'rxjs';
import {
  TReminderSeries,
  TReminderSeriesPayload,
  TReminderSeriesUpgradeReminderPayload,
} from 'src/app/project/modules/reminders/reminders.consts';
import {
  API_reminder_series,
  API_reminder_series_delete,
  API_reminder_series_single,
} from './reminder-series-paths';

@Injectable({
  providedIn: 'root',
})
export class ReminderSeriesApiProviderService {
  constructor(private apiService: ApiService) {}
  /**
   * @param reminder - The reminder series to create. It can be a new reminder or an upgrade to an existing reminder.
   * @returns recurring reminder series
   * @description This method creates a recurring reminder series. It can either create a new reminder or upgrade an existing reminder to a series.
   */
  createRecurringReminder(reminder: TReminderSeriesPayload): Observable<TReminderSeries> {
    const url = API_reminder_series();

    return this.apiService.post<TReminderSeries>(url, reminder);
  }

  /**
   * @param seriesId - The ID of the reminder series to retrieve.
   * @returns recurring reminder series
   * @description This method retrieves a specific recurring reminder series by its ID.
   */
  getReminderSeries(seriesId: string): Observable<TReminderSeries> {
    const url = API_reminder_series_single(seriesId);

    return this.apiService.get<TReminderSeries>(url);
  }

  /**
   * @param seriesId - The ID of the reminder series to update.
   * @param reminder - The updated reminder series data.
   * @returns recurring reminder series
   * @description This method updates an existing recurring reminder series.
   * It can be used to modify the details of a reminder series.
   */
  editReminderSeries(
    seriesId: string,
    reminder: TReminderSeriesUpgradeReminderPayload,
  ): Observable<TReminderSeries> {
    const url = API_reminder_series_single(seriesId);

    return this.apiService.put<TReminderSeries>(url, reminder);
  }

  /**
   *@param seriesId - The ID of the reminder series to delete.
   *@param reminderId - The ID of the reminder to delete. If not provided, the entire series will be deleted.
   *@returns 204 no content if reminder series got deleted successfully
   *  403 if user is not the owner/creator of the reminder series
   *  404 if reminder series with given ID doesn’t exist in database
   * @description This method deletes a recurring reminder series.
   * If a reminderId is provided, it will delete that specific reminder from the series.
   * If no reminderId is provided, it will delete the entire series.
   **/
  deleteReminderSeries(seriesId: string, reminderId?: string): Observable<void> {
    const url = API_reminder_series_delete(seriesId, reminderId);

    return this.apiService.delete<void>(url);
  }
}
